import React from 'react';
import {
  Box,
  Typography,
  FormControlLabel,
  Checkbox,
  Tooltip,
  Divider,
  Paper,
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';

/**
 * TeamSharingSection component for handling clinical session sharing with team members
 */
const normalizeSubRole = (subRole) => {
  if (!subRole) return '';
  if (typeof subRole === 'string') return subRole;
  if (Array.isArray(subRole)) {
    return subRole.map(normalizeSubRole).filter(Boolean).join(', ');
  }
  if (typeof subRole === 'object') {
    return (
      subRole.name ||
      subRole.label ||
      subRole.roleType ||
      subRole.path ||
      subRole.id ||
      ''
    );
  }
  return '';
};

const TeamSharingSection = ({
  viewOnlyAccess,
  teachersForStudent,
  includeParentGuardian,
  onAccessChange,
}) => {
  const peopleToShareWith = [
    ...teachersForStudent,
    includeParentGuardian
      ? {
          uid: 'Parent/Guardian',
          info: { fullname: 'Parent/Guardian' },
        }
      : null,
  ].filter(Boolean);

  const handleCheckboxChange = (personUid, isChecked) => {
    if (isChecked) {
      onAccessChange('viewOnlyAccess', [
        ...viewOnlyAccess,
        { uid: personUid, approved: true },
      ]);
    } else {
      onAccessChange(
        'viewOnlyAccess',
        viewOnlyAccess.filter((item) => item.uid !== personUid)
      );
    }
  };

  return (
    <Paper
      elevation={2}
      sx={{
        p: 3,
        borderRadius: 2,
        fontFamily: 'Lato, sans-serif',
      }}
    >
      <Divider sx={{ mb: 2, borderBottomWidth: 2 }} />
      <Typography
        sx={{
          fontWeight: 'bold',
          fontFamily: 'Lato, sans-serif',
          color: '#9291f0',
          mb: 2,
        }}
      >
        Share with team
      </Typography>

      <Typography
        variant='subtitle2'
        sx={{
          fontWeight: 'bold',
          mt: 2,
          mb: 1,
          fontFamily: 'Lato, sans-serif',
        }}
      >
        View Access:
      </Typography>
      {peopleToShareWith.length === 0 ? (
        <Typography
          sx={{
            fontStyle: 'italic',
            color: 'gray',
            mb: 2,
            fontFamily: 'Lato, sans-serif',
          }}
        >
          There is no one to share the session with.
        </Typography>
      ) : (
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            mb: 2,
            fontFamily: 'Lato, sans-serif',
          }}
        >
          {peopleToShareWith.map((person) => {
            const personUid = person?.uid;
            const subRoleLabel = normalizeSubRole(person?.info?.subRoleName);
            const showTooltip = Boolean(subRoleLabel);
            const isChecked = viewOnlyAccess.some(
              (item) => item.uid === personUid
            );

            return (
              <FormControlLabel
                key={`viewOnlyAccess-${personUid}`}
                control={
                  <Checkbox
                    checked={isChecked}
                    onChange={(e) =>
                      handleCheckboxChange(personUid, e.target.checked)
                    }
                  />
                }
                label={
                  <span style={{ fontFamily: 'Lato, sans-serif' }}>
                    {person?.info?.fullname}
                    {showTooltip && (
                      <Tooltip title={subRoleLabel} arrow placement='top'>
                        <InfoIcon
                          sx={{
                            fontSize: 'small',
                            ml: 0.5,
                            verticalAlign: 'middle',
                            color: 'action.active',
                            cursor: 'help',
                          }}
                        />
                      </Tooltip>
                    )}
                  </span>
                }
                sx={{ mr: 2 }}
              />
            );
          })}
        </Box>
      )}
    </Paper>
  );
};

export default TeamSharingSection;
